import React, { useCallback, useEffect } from 'react';
import noop from 'lodash/noop';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import { SELECTED_COMMENT_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useUpdateCommentsCellCache } from '../use-update-comments-cell-cache/index.tsx';
import type { OnOpenAnalyticAttributes } from '../../ui/list-view/comments-cell/index.tsx';

export type CommentsSidebarProps = {
	issueKey: IssueKey;
	onClose: () => void;
};

export const useCommentsSidebar = ({
	CommentsSidebar,
}: {
	CommentsSidebar: React.FC<CommentsSidebarProps>;
}) => {
	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_COMMENT_PARAM);

	const [, { setSidebarContent }] = useSidebar();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { updateCommentsCache } = expVal('jira_unread_comments_in_list_view', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useUpdateCommentsCellCache()
		: { updateCommentsCache: noop };

	// set query param when comment cell is clicked
	const onOpen = useCallback(
		// Remove optional on analyticsAttributes when jira_unread_comments_in_list_view experiment is cleaned up
		(issueKey: string, analyticAttributes?: OnOpenAnalyticAttributes) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'commentsSidebar opened', {
				...(analyticAttributes && fg('_jira_unread_comments_in_list_view_gate')
					? analyticAttributes
					: {}),
			});
			setSelectedIssueKey(issueKey);

			if (expVal('jira_unread_comments_in_list_view', 'isEnabled', false)) {
				updateCommentsCache(issueKey);
			}
		},
		[createAnalyticsEvent, setSelectedIssueKey, updateCommentsCache],
	);

	// close comment sidebar on [X] button click
	const onClose = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'commentsSidebar closed');
		setSelectedIssueKey(undefined);
		setSidebarContent(null);
	}, [createAnalyticsEvent, setSelectedIssueKey, setSidebarContent]);

	// set sidebar content after query param is set
	useEffect(() => {
		if (selectedIssueKey) {
			const newContent = <CommentsSidebar issueKey={selectedIssueKey} onClose={onClose} />;
			setSidebarContent(newContent);
		}
	}, [CommentsSidebar, onClose, selectedIssueKey, setSidebarContent]);

	return {
		selectedIssueKey,
		onOpenCommentsSidebar: onOpen,
		onCloseCommentsSidebar: onClose,
	};
};
